/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2, breakpoints } from '@coursera/cds-core';

import type { Domain } from 'bundles/browse/components/Domain/types/Domain';
import type { Subdomain } from 'bundles/browse/components/Domain/types/SubDomain';
import { TrackedA } from 'bundles/page/components/TrackedLink2';

const SubdomainPillHeight = 48;

const styles = {
  subdomainCarouselCLP: css`
    margin-top: 13px;
    position: relative;
    z-index: 0;

    .subdomain-carousel-careers-container,
    .subdomain-carousel-topics-container {
      .popular-careers-container,
      .popular-topics-container {
        margin: 0 0 10px 15px;

        .popular-careers-links {
          margin-top: 1px;
        }

        .rc-SubdomainItem.menu-item {
          &:hover {
            color: var(--cds-color-blue-700);
            text-decoration: none;
          }
        }
      }
    }

    /* stylelint-disable-next-line no-duplicate-selectors */
    .subdomain-carousel-careers-container,
    .subdomain-carousel-topics-container {
      z-index: 1;
      width: 400px;
      max-height: 184px;
      margin: 0 20px 10px 0;
      background-color: var(--cds-color-neutral-background-primary);
      border-radius: 5px;

      ${breakpoints.down('md')} {
        /* $smaller-than-tablet */
        width: 100%;
        height: 100%;
      }

      .subdomain-carousel-title {
        font-size: 14px;

        /* c-opensans('Semibold') */
        margin: 10px 0 5px 15px;
        color: var(--cds-color-neutral-primary);

        ${breakpoints.down('md')} {
          /* $smaller-than-tablet */
          margin-left: 15px;
        }
      }

      .popular-careers-container {
        font-size: 14px;
      }
    }
  `,
  subdomainCarousel: css`
    margin-top: 24px;
    margin-bottom: 32px;
    display: flex;
    flex-wrap: wrap;
    position: relative;

    ${breakpoints.down('md')} {
      max-width: 90vw;
    }

    ${breakpoints.down('xs')} {
      display: block;
    }

    .subdomain-link {
      margin-right: 12px;
      margin-bottom: 8px;
    }

    .subdomain-pill {
      padding: 0 24px;
      height: ${SubdomainPillHeight}px;
      background-color: var(--cds-color-neutral-background-primary);
      text-align: center;
      color: var(--cds-color-neutral-primary);
      text-transform: capitalize;
      font-size: 16px;
      border-radius: 3px;
      display: inline-block;
      width: max-content;
      ${breakpoints.down('md')} {
        font-size: 14px;
        padding: 0 12px;
      }

      &:hover {
        color: var(--cds-color-blue-700);
        text-decoration: none;
      }

      h2 {
        line-height: ${SubdomainPillHeight}px;
        margin: 0;
      }
    }
  `,
};

type Props = {
  domain: Domain;
  subdomains: Array<Subdomain>;
  /**
   * A callback function for generating the domain page url.
   */
  onGetSubdomainPageUrl?: (domain: Domain, subdomain: Subdomain) => string;

  /**
   * Custom subdomain card click handler
   */
  handleSubdomainClick?: (subdomain: Subdomain) => (event: React.MouseEvent<HTMLAnchorElement>) => void;
};

export const SubdomainCarousel = (props: Props) => {
  const { domain, subdomains, onGetSubdomainPageUrl, handleSubdomainClick } = props;
  const getSubdomainPageUrl = (subdomain: Subdomain) => {
    if (onGetSubdomainPageUrl) {
      return onGetSubdomainPageUrl(domain, subdomain);
    } else {
      return `/browse/${domain.slug || domain.id}/${subdomain.id}`;
    }
  };

  return (
    <div className="rc-SubdomainCarousel" css={styles.subdomainCarousel}>
      {subdomains?.map((subdomainObj) => (
        <div key={subdomainObj.id} data-e2e="subdomain-pill" className="subdomain-link">
          <TrackedA
            href={getSubdomainPageUrl(subdomainObj)}
            data={{ subdomain: subdomainObj.id }}
            trackingName="subdomain_pill"
            className="subdomain-pill noStyle"
            onClick={handleSubdomainClick?.(subdomainObj)}
          >
            <Typography2 variant="bodySecondary" component="h2" role="none">
              {subdomainObj.name}
            </Typography2>
          </TrackedA>
        </div>
      ))}
    </div>
  );
};

export default SubdomainCarousel;
